import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Send as SendIcon,
  Upload as UploadIcon,
  AttachMoney as MoneyIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { format, addMonths, addDays } from 'date-fns';
import MobileRecordCard from '../components/common/MobileRecordCard';

interface BillingRecord {
  id: string;
  internalId: string;
  patientName: string;
  phone: string;
  email: string;
  feeStructure: 'monthly' | 'quarterly' | 'biannual' | 'annual';
  feeAmount: number;
  effectiveDate: string;
  renewalMonth: string;
  nextPaymentDue: string;
  paymentStatus: 'paid' | 'pending' | 'overdue';
  lastVisitDate?: string;
  lastVisitType?: string;
  notes?: string;
}

// Mock data based on the user's Excel screenshots
const mockBillingRecords: BillingRecord[] = [
  {
    id: '1',
    internalId: 'LMD-00001',
    patientName: 'John Smith',
    phone: '+13056994969',
    email: 'john.smith@email.com',
    feeStructure: 'biannual',
    feeAmount: 2400,
    effectiveDate: '2024-01-15',
    renewalMonth: 'July',
    nextPaymentDue: '2025-01-15',
    paymentStatus: 'paid',
    lastVisitDate: '2024-12-10',
    lastVisitType: 'Wellness Exam',
  },
  {
    id: '2',
    internalId: 'LMD-00002',
    patientName: 'Sarah Johnson',
    phone: '+17865551234',
    email: 'sarah.j@email.com',
    feeStructure: 'monthly',
    feeAmount: 200,
    effectiveDate: '2024-03-01',
    renewalMonth: 'Monthly',
    nextPaymentDue: '2025-01-01',
    paymentStatus: 'pending',
    lastVisitDate: '2024-11-20',
    lastVisitType: 'Follow Up',
  },
  {
    id: '3',
    internalId: 'LMD-00003',
    patientName: 'Michael Brown',
    phone: '+13057778888',
    email: 'mbrown@email.com',
    feeStructure: 'annual',
    feeAmount: 4800,
    effectiveDate: '2024-06-01',
    renewalMonth: 'June',
    nextPaymentDue: '2024-12-15',
    paymentStatus: 'overdue',
    lastVisitDate: '2024-09-15',
    lastVisitType: 'Urgent Visit',
    notes: 'Payment plan requested',
  },
];

const Billing: React.FC = () => {
  // App chrome + table->card switches key off `down('md')` (see plan).
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [records, setRecords] = useState<BillingRecord[]>(mockBillingRecords);
  const [searchTerm, setSearchTerm] = useState('');
  const [feeStructureFilter, setFeeStructureFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState<'list' | 'summary'>('list');
  const [selectedRecord, setSelectedRecord] = useState<BillingRecord | null>(null);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const filteredRecords = records.filter(record => {
    if (feeStructureFilter !== 'all' && record.feeStructure !== feeStructureFilter) return false;
    if (statusFilter !== 'all' && record.paymentStatus !== statusFilter) return false;
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      return (
        record.patientName.toLowerCase().includes(query) ||
        record.phone.includes(query) ||
        record.internalId.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const handleSendReminder = (record: BillingRecord) => {
    setSelectedRecord(record);
    setReminderDialogOpen(true);
  };

  const handleUploadInvoice = (record: BillingRecord) => {
    setSelectedRecord(record);
    setUploadDialogOpen(true);
  };

  // Calculate summary stats
  const totalRevenue = records.reduce((sum, r) => sum + r.feeAmount, 0);
  const paidCount = records.filter(r => r.paymentStatus === 'paid').length;
  const pendingCount = records.filter(r => r.paymentStatus === 'pending').length;
  const overdueCount = records.filter(r => r.paymentStatus === 'overdue').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Billing
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage patient billing, invoices, and payment reminders
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              ${totalRevenue.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Monthly Revenue
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {paidCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Paid
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              {pendingCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="error.main">
              {overdueCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overdue
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search patients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: { xs: '48%', sm: 150 } }}>
            <InputLabel>Fee Structure</InputLabel>
            <Select
              value={feeStructureFilter}
              label="Fee Structure"
              onChange={(e) => setFeeStructureFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="biannual">Bi-Annual</MenuItem>
              <MenuItem value="annual">Annual</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: '48%', sm: 120 } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Billing list: stacked cards on mobile, full table on desktop */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {filteredRecords.map((record) => (
            <MobileRecordCard
              key={record.id}
              title={record.patientName}
              subtitle={[record.internalId, record.phone].filter(Boolean).join(' · ')}
              status={{ label: record.paymentStatus, color: getStatusColor(record.paymentStatus) as any }}
              rows={[
                { label: 'Amount', value: `$${record.feeAmount.toLocaleString()}` },
                {
                  label: 'Fee',
                  value: (
                    <Box component="span" sx={{ textTransform: 'capitalize' }}>
                      {record.feeStructure}
                    </Box>
                  ),
                },
                { label: 'Next Due', value: format(new Date(record.nextPaymentDue), 'MMM d, yyyy') },
                {
                  label: 'Last Visit',
                  value: record.lastVisitDate
                    ? [format(new Date(record.lastVisitDate), 'MMM d, yyyy'), record.lastVisitType]
                        .filter(Boolean)
                        .join(' · ')
                    : '—',
                },
              ]}
              actions={
                <>
                  <Button size="small" variant="outlined" startIcon={<SendIcon />} onClick={() => handleSendReminder(record)}>
                    Remind
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<UploadIcon />} onClick={() => handleUploadInvoice(record)}>
                    Invoice
                  </Button>
                </>
              }
            />
          ))}
          {filteredRecords.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No billing records match the current filters.
            </Typography>
          )}
        </Box>
      ) : (
      <Paper sx={{ overflow: 'auto' }}>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Patient ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Fee Structure</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Next Payment Due</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Visit</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map(record => (
                <TableRow key={record.id} hover>
                  <TableCell>{record.internalId}</TableCell>
                  <TableCell>{record.patientName}</TableCell>
                  <TableCell>{record.phone}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {record.feeStructure}
                  </TableCell>
                  <TableCell>${record.feeAmount.toLocaleString()}</TableCell>
                  <TableCell>
                    {format(new Date(record.nextPaymentDue), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={record.paymentStatus}
                      size="small"
                      color={getStatusColor(record.paymentStatus) as any}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    {record.lastVisitDate ? (
                      <Box>
                        <Typography variant="body2">
                          {format(new Date(record.lastVisitDate), 'MMM d, yyyy')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {record.lastVisitType}
                        </Typography>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SendIcon />}
                        onClick={() => handleSendReminder(record)}
                      >
                        Remind
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => handleUploadInvoice(record)}
                      >
                        Invoice
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {/* Send Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onClose={() => setReminderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Payment Reminder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Send an SMS reminder to <strong>{selectedRecord?.patientName}</strong> at {selectedRecord?.phone}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            defaultValue={selectedRecord ? 
              `Hi ${selectedRecord.patientName.split(' ')[0]}, this is a reminder that your payment of $${selectedRecord.feeAmount.toLocaleString()} is due on ${format(new Date(selectedRecord.nextPaymentDue), 'MMMM d, yyyy')}. Please contact our office if you have any questions. Thank you!`
              : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon />}>
            Send via Spruce
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Invoice Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Upload an invoice for <strong>{selectedRecord?.patientName}</strong>
          </Typography>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography>Click or drag to upload PDF invoice</Typography>
            <Typography variant="caption" color="text.secondary">
              Link will expire in 180 days
            </Typography>
          </Box>
          <TextField
            fullWidth
            label="Description (optional)"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<UploadIcon />}>
            Upload & Send Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;


